import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Menu,
  ChevronDown,
  LogOut,
  LayoutDashboard,
  Car,
  Heart,
  Shield,
  BookOpen,
  Users,
  Truck,
  Bell,
  User,
  Settings,
  HelpCircle,
  Clock
} from "lucide-react";
import { useState } from "react";

const MobileNavbar = ({
  isOpen,
  setIsOpen,
  isActivePath,
  mobileSubmenuOpen,
  handleMobileSubmenuClick,
  volunteerLinks,
  vehicleLinks,
  handleMobileLinkClick,
  handleRegisterClick,
  user,
  handleLogoutClick,
  handleDashboardClick
}) => {
  const [mobileVehicleSubmenuOpen, setMobileVehicleSubmenuOpen] = useState(false);

  const handleMobileVehicleSubmenuClick = () => {
    setMobileVehicleSubmenuOpen(!mobileVehicleSubmenuOpen);
  };

  const closeMenu = () => setIsOpen(false);

  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden flex items-center">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 rounded-lg transition-all duration-200 text-red-600 hover:bg-red-50 hover:shadow-md"
          aria-label="Toggle menu"
        >
          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <X className="h-5 w-5" />
              </motion.div>
            ) : (
              <motion.div
                key="menu"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Menu className="h-5 w-5" />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={closeMenu}
          />
        )}
      </AnimatePresence>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="lg:hidden fixed top-0 right-0 h-full w-80 max-w-[85vw] z-50 bg-white shadow-2xl"
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={closeMenu}
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  <X className="h-4 w-4" />
                </motion.button>
              </div>

              {/* Menu Content */}
              <div className="flex-1 overflow-y-auto py-4">
                <div className="space-y-2 px-4">
                  {/* Main Navigation Links */}
                  <MobileNavLink
                    to="/"
                    isActive={isActivePath('/')}
                    icon={<Heart className="w-4 h-4" />}
                    onClick={closeMenu}
                  >
                    Home
                  </MobileNavLink>

                  <MobileNavLink
                    to="/emergency-blood"
                    isActive={isActivePath('/emergency-blood')}
                    icon={<Shield className="w-4 h-4" />}
                    badge="Emergency"
                    onClick={closeMenu}
                  >
                    Emergency Blood
                  </MobileNavLink>

                  <MobileNavLink
                    to="/projects"
                    isActive={isActivePath('/projects')}
                    icon={<BookOpen className="w-4 h-4" />}
                    onClick={closeMenu}
                  >
                    Donate Campaigns
                  </MobileNavLink>

                  {/* Volunteer Services Dropdown */}
                  <MobileDropdown
                    isOpen={mobileSubmenuOpen}
                    onToggle={handleMobileSubmenuClick}
                    isActive={isActivePath('/volunteer') || isActivePath('/ngo-registration') ||
                      isActivePath('/register-vehicle') || isActivePath('/hospital-login')}
                    icon={<Users className="w-4 h-4" />}
                    title="Volunteer Services"
                  >
                    <div className="px-4 py-2 bg-gray-50 rounded-lg mx-2 mb-2">
                      <p className="text-xs text-gray-600">Join our community to help save lives</p>
                    </div>
                    {volunteerLinks.map((link) => (
                      <MobileDropdownLink
                        key={link.name}
                        to={link.path}
                        onClick={() => {
                          handleMobileLinkClick(link);
                          closeMenu();
                        }}
                        isActive={isActivePath(link.path)}
                        icon={link.name.includes("NGO") && link.name.includes("Login") ? <User className="w-4 h-4" /> :
                          link.name.includes("NGO") ? <Users className="w-4 h-4" /> :
                            link.name.includes("Hospital") && link.name.includes("Login") ? <User className="w-4 h-4" /> :
                              link.name.includes("Hospital") ? <Shield className="w-4 h-4" /> :
                                <Car className="w-4 h-4" />}
                      >
                        {link.name}
                      </MobileDropdownLink>
                    ))}
                  </MobileDropdown>

                  {/* Vehicle Services Dropdown */}
                  <MobileDropdown
                    isOpen={mobileVehicleSubmenuOpen}
                    onToggle={handleMobileVehicleSubmenuClick}
                    isActive={isActivePath('/vehicle/login') || isActivePath('/vehicle/register') ||
                      isActivePath('/vehicle/dashboard')}
                    icon={<Truck className="w-4 h-4" />}
                    title="Vehicle Services"
                  >
                    <div className="px-4 py-2 bg-gray-50 rounded-lg mx-2 mb-2">
                      <p className="text-xs text-gray-600">Emergency transportation services</p>
                    </div>

                    {/* Emergency Services */}
                    <div className="px-4 py-1">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Emergency Services
                      </div>
                      {vehicleLinks.filter(link => !link.group).map((link) => (
                        <MobileDropdownLink
                          key={link.name}
                          to={link.path}
                          onClick={() => {
                            handleMobileLinkClick(link);
                            closeMenu();
                          }}
                          isActive={isActivePath(link.path)}
                          icon={link.icon === 'car' ? <Car className="w-4 h-4" /> :
                            link.icon === 'search' ? <Clock className="w-4 h-4" /> :
                              <Bell className="w-4 h-4" />}
                        >
                          {link.name}
                        </MobileDropdownLink>
                      ))}
                    </div>

                    {/* For Drivers */}
                    <div className="px-4 py-1 border-t border-gray-100 mt-2">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        For Drivers
                      </div>
                      {vehicleLinks.filter(link => link.group === 'owner').map((link) => (
                        <MobileDropdownLink
                          key={link.name}
                          to={link.path}
                          onClick={() => {
                            handleMobileLinkClick(link);
                            closeMenu();
                          }}
                          isActive={isActivePath(link.path)}
                          icon={link.icon === 'login' ? <User className="w-4 h-4" /> :
                            link.icon === 'user-plus' ? <Users className="w-4 h-4" /> :
                              <LayoutDashboard className="w-4 h-4" />}
                        >
                          {link.name}
                        </MobileDropdownLink>
                      ))}
                    </div>
                  </MobileDropdown>
                </div>

                {/* User Section */}
                {user && (
                  <div className="mt-6 px-4">
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{user.name}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            handleDashboardClick();
                            closeMenu();
                          }}
                          className="w-full flex items-center px-4 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                        >
                          <LayoutDashboard className="mr-3 w-4 h-4" />
                          Dashboard
                        </motion.button>

                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          as={Link}
                          to="/user-profile"
                          onClick={closeMenu}
                          className="w-full flex items-center px-4 py-3 rounded-lg border border-gray-300 text-gray-700 hover:border-blue-600 hover:text-blue-600 transition-colors"
                        >
                          <User className="mr-3 w-4 h-4" />
                          Profile
                        </motion.button>

                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            handleLogoutClick();
                            closeMenu();
                          }}
                          className="w-full flex items-center px-4 py-3 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
                        >
                          <LogOut className="mr-3 w-4 h-4" />
                          Logout
                        </motion.button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Auth Buttons for non-logged in users */}
                {!user && (
                  <div className="mt-6 px-4">
                    <div className="border-t border-gray-200 pt-4">
                      <div className="space-y-3">
                        <Link
                          to="/login"
                          onClick={closeMenu}
                          className="w-full flex items-center justify-center px-4 py-3 rounded-lg border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-colors transition-all duration-200"
                        >
                          <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="w-full flex items-center justify-center"
                          >
                            Login
                          </motion.div>
                        </Link>

                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            handleRegisterClick();
                            closeMenu();
                          }}
                          className="w-full flex items-center justify-center px-4 py-3 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
                        >
                          Register
                        </motion.button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Reusable Mobile Components
const MobileNavLink = ({ to, children, isActive, icon, badge, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`flex items-center px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${isActive
      ? 'text-red-600 bg-red-50 border-l-4 border-red-600'
      : 'text-gray-700 hover:text-red-600 hover:bg-red-50 hover:border-l-4 hover:border-red-600'
      }`}
  >
    <div className="flex items-center space-x-2.5">
      {icon}
      <span>{children}</span>
      {badge && (
        <span className="px-2 py-0.5 text-[10px] rounded-full bg-red-100 text-red-600 font-semibold">
          {badge}
        </span>
      )}
    </div>
  </Link>
);

const MobileDropdown = ({ isOpen, onToggle, isActive, icon, title, children }) => (
  <div className="space-y-2">
    <button
      onClick={onToggle}
      className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${isActive
        ? 'text-red-600 bg-red-50 border-l-4 border-red-600'
        : 'text-gray-700 hover:text-red-600 hover:bg-red-50 hover:border-l-4 hover:border-red-600'
        }`}
    >
      <div className="flex items-center space-x-2.5">
        {icon}
        <span>{title}</span>
      </div>
      <motion.div
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: 0.2 }}
      >
        <ChevronDown className="w-4 h-4" />
      </motion.div>
    </button>

    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <div className="space-y-1 pl-4">
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const MobileDropdownLink = ({ to, children, onClick, isActive, icon }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`flex items-center px-4 py-2 rounded-xl text-sm transition-colors ${isActive
      ? 'text-red-600 bg-red-50'
      : 'text-gray-700 hover:bg-red-50 hover:text-red-600'
      }`}
  >
    <div className="flex items-center space-x-2.5">
      {icon}
      <span>{children}</span>
    </div>
  </Link>
);

export default MobileNavbar;